/**
 * What the state and transition dialogs decide, pure - issue #50.
 *
 * A transition LIVES in the state that contains both its ends - the machine -
 * and a person works from one end: on a state, a way out of it (to) or a way
 * in (from). So a dialog asks for the direction and the OTHER state, and the
 * server decides where the edge is declared. What the dialogs need to know
 * here is only which other states that rule can accept, so they are not
 * offered one the server would refuse.
 */

import type { EntityRow, TouchingTransition } from '../../schema'
import { declaringState } from '../canvas/transitionEdits'
import type { StateRef } from '../canvas/transitionEdits'

export type Direction = 'to' | 'from'

/** The kinds of state a new one may be, and the type each specializes. */
export const STATE_SORTS = [
  { value: 'EchelonState', label: 'State' },
  { value: 'EchelonMode', label: 'Mode' },
  { value: 'EchelonPhase', label: 'Phase' },
] as const

export function stateRefs(rows: EntityRow[]): StateRef[] {
  return rows.map((row) => ({ id: row.id, qualified_name: String(row.qualified_name ?? '') }))
}

/**
 * Whether a transition between two states can be declared anywhere.
 *
 * The canvas's rule, which the server applies too: the innermost state
 * enclosing both. None means different machines, or a top-level machine as an
 * end - decision 019's refusal of a "from any mode" edge.
 */
export function canJoin(states: StateRef[], one: string, other: string): boolean {
  return declaringState(states, one, other).allowed
}

/**
 * Whether a part is OFFERED as a decider: a usage - the flight software in
 * this vehicle, not the FlightSoftware kind (Patrick, 2026-09-28, #49).
 *
 * Offered, not enforced: the server accepts any part, so a definition a file
 * already names is not flagged. It is just not suggested.
 */
export function offeredAsDecider(row: EntityRow): boolean {
  return row.is_definition === false
}

/** Which way an edge goes, seen from one of its ends. */
export function directionOf(edge: TouchingTransition, state: string): Direction {
  return edge.from === state ? 'to' : 'from'
}

/** The state a machine starts in now, if it states one. */
export function currentStart(states: EntityRow[], machine: string): EntityRow | undefined {
  return states.find((row) => String(row.owner ?? '') === machine && row.is_initial === true)
}

/** A state's display name. */
export function stateName(row: EntityRow | undefined, fallback = ''): string {
  return row ? String(row.name || row.id) : fallback
}
